import React from 'react';
import { motion } from 'framer-motion';
import ContactImage from "../image/contactImage.png";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import ContactForm from '../component/ContactForm';

const Contact = () => {
  const fadeIn = (direction = "up", delay = 0) => ({
    hidden: {
      opacity: 0,
      y: direction === "up" ? 40 : direction === "down" ? -40 : 0,
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay,
        ease: "easeOut",
      },
    },
  });

  const contactInfo = [
    {
      icon: FaPhoneAlt,
      title: "Phone",
      details: ["+250 784 056 256"],
      link: "tel:+250784056256",
      color: "from-green-500 to-green-600",
    },
    {
      icon: FaEnvelope,
      title: "Email",
      details: ["kigalianimalfeed@gmail.com", "info@kigalianimalfeeds.com"],
      link: "mailto:kigalianimalfeed@gmail.com",
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: FaMapMarkerAlt,
      title: "Location",
      details: ["Rwanda, Kigali, Nyabugogo"],
      link: "https://maps.google.com/?q=-1.937722,30.05075",
      color: "from-red-500 to-red-600",
    },
    {
      icon: FaClock,
      title: "Business Hours",
      details: ["Mon - Fri: 8:00 AM - 6:00 PM", "Sat: 8:00 AM - 4:00 PM"],
      link: null,
      color: "from-purple-500 to-purple-600",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative bg-cover bg-center text-white mt-20 md:mt-24"
        style={{ backgroundImage: `url(${ContactImage})` }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-primary/50 to-black/70"></div>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 py-20 md:py-28 px-6 md:px-16 lg:px-40">
          <motion.div
            variants={fadeIn("down")}
            initial="hidden"
            animate="show"
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Get in{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-green-500">
                Touch
              </span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              We're here to help! Reach out to us for any inquiries about our products and services.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="container mx-auto px-6 md:px-16 lg:px-40 -mt-12 relative z-20 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactInfo.map((info, index) => (
            <motion.div
              key={index}
              variants={fadeIn("up", index * 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ y: -8 }}
              className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                <info.icon className="text-white text-xl" />
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-gray-800 mb-3">
                {info.title}
              </h3>

              {/* Details */}
              <div className="space-y-1">
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-gray-600 text-sm">
                    {info.link && idx === 0 ? (
                      <a
                        href={info.link}
                        target={info.link.startsWith('http') ? "_blank" : undefined}
                        rel={info.link.startsWith('http') ? "noopener noreferrer" : undefined}
                        className="hover:text-primary transition-colors"
                      >
                        {detail}
                      </a>
                    ) : (
                      detail
                    )}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-gradient-to-b from-white to-secondary/30 py-16">
        <ContactForm />
      </div>

      {/* FAQ Section (Optional) */}
      <div className="container mx-auto px-6 md:px-16 lg:px-40 py-16">
        <motion.div
          variants={fadeIn("up", 0.2)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Find answers to common questions about our products and services
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4">
          {[
            {
              question: "What types of animal feed do you offer?",
              answer: "We offer a comprehensive range of animal feeds including poultry feed, pig feed, dairy cattle feed, and various raw materials for custom feed formulations.",
            },
            {
              question: "Do you provide delivery services?",
              answer: "Yes, we provide delivery services across Kigali and surrounding areas. Contact us for more details about delivery options and costs.",
            },
            {
              question: "Can I get technical advice on livestock farming?",
              answer: "Absolutely! Our experienced team provides technical services and best practice guidance for livestock farming, especially for chickens, pigs, and dairy cattle.",
            },
            {
              question: "What are your payment options?",
              answer: "We accept various payment methods including cash, mobile money, and bank transfers. Contact us for specific payment arrangements.",
            },
          ].map((faq, index) => (
            <motion.div
              key={index}
              variants={fadeIn("up", index * 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="group bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary transition-colors">
                {faq.question}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {faq.answer}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Contact;
